/**
 * Post-processing algorithms for SDF volumes.
 *
 * This module provides:
 * - Small part removal using connected component labeling
 * - Keep-N-largest component selection
 * - Cavity filling (3D via connected component labeling, 2D via per-slice
 *   border flood fill)
 *
 * Units: every physical quantity crossing this boundary is in domain units
 * (mm, mm², mm³). The cm / cm² / cm³ the user sees are converted at the UI
 * boundary — nothing below this line knows about cm.
 */

import {
    VoiShape,
    VoxelBounds,
    regionInsideDistanceMm,
} from '~/domain/services/imageService/gridGeometry';
import { SDF_ISO_THRESHOLD } from './sdfConstants';

import { maybeYield } from './utils';

/**
 * Connectivity mode for post-processing operations:
 * - '3D' operates on the whole volume (components link across slices).
 * - '2D' operates on each native image slice independently.
 *
 * The "slice" plane is the SDF grid's third axis, which follows the source
 * image's native slice direction — transversal for a standard transversal acquisition,
 * but not guaranteed to be anatomically transversal for oblique or reformatted
 * grids.
 *
 * This is the mode the user-facing operations (`removeSmallParts`,
 * `fillCavities`) take. The lower-level labeling primitives take the
 * equivalent {@link ConnectedComponentOptions.connectivity}; the wrappers
 * map one onto the other.
 */
export type PostProcessingMode = '2D' | '3D';

/**
 * Options shared by the connected-component based operations in this module.
 */
export interface ConnectedComponentOptions {
    /**
     * Neighbourhood used for connectivity. '3d' (the default) is 6-connected
     * across X/Y/Z; '2d' drops the Z neighbour so every slice is labeled
     * independently (4-connected in-plane) — components never span slices.
     */
    connectivity?: '2d' | '3d';
    /**
     * Disables the SDF skip optimization. The skip assumes a *metric* SDF
     * (values fall off smoothly near the surface) and jumps 3 voxels ahead
     * where the field reads "far from surface" — on a hard binary 0/255 mask
     * every background voxel reads "far", so runs of voxels would go
     * unvisited and background labeling (cavity detection) would be wrong.
     * Set true whenever the input is a thresholded mask rather than a
     * reinitialized SDF.
     */
    disableSdfSkip?: boolean;
}

/** Maps the user-facing mode onto the labeler's connectivity option. */
function connectivityForMode(mode: PostProcessingMode): '2d' | '3d' {
    return mode === '2D' ? '2d' : '3d';
}

/**
 * Connected component labeling using union-find, working directly on SDF data.
 * Returns an array of labels (0 = background, 1+ = component ID).
 *
 * Connectivity is face-only (6-connected) in 3D. In '2d' the cross-slice (Z)
 * neighbour link is dropped, so each native slice is labelled independently
 * (4-connectivity within the slice) and no component ever spans slices.
 *
 * `componentSizes[label]` is the voxel count of each final label (index 0
 * unused) — counted during the flatten pass, so callers never need a
 * separate full-volume counting pass.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param labelInside If true, labels voxels >= threshold; if false, labels voxels < threshold
 * @param signal Optional AbortSignal for cancellation
 * @param options Connectivity mode and skip-optimization control
 */
export async function labelConnectedComponents(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    labelInside: boolean,
    signal?: AbortSignal,
    options?: ConnectedComponentOptions
): Promise<{
    labels: Int32Array;
    numComponents: number;
    componentSizes: Int32Array;
}> {
    const connect3d = (options?.connectivity ?? '3d') === '3d';
    const useSdfSkip = !options?.disableSdfSkip;
    const N = nx * ny * nz;
    const plane = nx * ny;
    const labels = new Int32Array(N);
    const parent = new Int32Array(N);
    // Rank array not needed - use weighted union by size instead, stored in parent as negative values
    // parent[i] >= 0 means parent[i] is the parent index
    // parent[i] < 0 means i is a root with size = -parent[i]

    // Precalculate neighbor offsets
    const offsetX = 1;
    const offsetY = nx;
    const offsetZ = plane;

    // SDF skip thresholds - if far from surface, skip ahead
    const skipLowThreshold = 16; // Definitely outside
    const skipHighThreshold = 240; // Definitely inside
    const skipAmount = 3;

    // Initialize all as roots with size 1 (stored as -1)
    parent.fill(-1);

    // Find with path compression (iterative to avoid stack overflow)
    function find(x: number): number {
        let root = x;
        while (parent[root] >= 0) {
            root = parent[root];
        }
        // Path compression
        while (parent[x] >= 0) {
            const next = parent[x];
            parent[x] = root;
            x = next;
        }
        return root;
    }

    // Union by size (size stored as negative value in root's parent)
    function union(x: number, y: number): void {
        const rootX = find(x);
        const rootY = find(y);
        if (rootX !== rootY) {
            // parent[root] is negative size, so smaller (more negative) = larger set
            if (parent[rootX] <= parent[rootY]) {
                // rootX has larger or equal size
                parent[rootX] += parent[rootY]; // Merge sizes
                parent[rootY] = rootX;
            } else {
                parent[rootY] += parent[rootX];
                parent[rootX] = rootY;
            }
        }
    }

    // Helper to check if voxel is foreground based on labelInside mode
    const isForeground = labelInside
        ? (val: number) => val >= threshold
        : (val: number) => val < threshold;

    // The "skip-worthy" condition: far from surface in the direction we DON'T want
    const canSkip = labelInside
        ? (val: number) => val < skipLowThreshold // Far outside, skip
        : (val: number) => val > skipHighThreshold; // Far inside, skip

    let lastYieldTick = performance.now();

    // First pass: assign provisional labels and record equivalences
    for (let z = 0; z < nz; z++) {
        const slice = sdfU8[z];
        const zOffset = z * plane;

        for (let y = 0; y < ny; y++) {
            const yOffset = y * nx;

            for (let x = 0; x < nx; x++) {
                const localIdx = yOffset + x;
                const i = zOffset + localIdx;
                const val = slice[localIdx];

                // SDF skip optimization: if far from surface in background direction, skip ahead
                if (useSdfSkip && canSkip(val)) {
                    const skip = Math.min(skipAmount, nx - 1 - x);
                    if (skip > 0) {
                        x += skip - 1; // -1 because loop will increment
                        continue;
                    }
                }

                if (!isForeground(val)) {
                    continue;
                } // Background

                // Check 6-connected neighbors (only those already visited)
                // Using precalculated offsets instead of idx3() calls.
                // In '2d' the cross-slice link is dropped so components are
                // labelled independently per native slice.
                const nX =
                    x > 0 && isForeground(slice[localIdx - 1])
                        ? i - offsetX
                        : -1;
                const nY =
                    y > 0 && isForeground(slice[localIdx - nx])
                        ? i - offsetY
                        : -1;
                const nZ =
                    connect3d && z > 0 && isForeground(sdfU8[z - 1][localIdx])
                        ? i - offsetZ
                        : -1;

                if (nX < 0 && nY < 0 && nZ < 0) {
                    // New component - use own index as label
                    labels[i] = i;
                } else {
                    // Find minimum neighbor label
                    let minLabel = N; // Start with max possible
                    if (nX >= 0 && labels[nX] < minLabel) {
                        minLabel = labels[nX];
                    }
                    if (nY >= 0 && labels[nY] < minLabel) {
                        minLabel = labels[nY];
                    }
                    if (nZ >= 0 && labels[nZ] < minLabel) {
                        minLabel = labels[nZ];
                    }

                    labels[i] = minLabel;

                    // Union all neighbor labels with minLabel
                    if (nX >= 0) {
                        union(minLabel, labels[nX]);
                    }
                    if (nY >= 0) {
                        union(minLabel, labels[nY]);
                    }
                    if (nZ >= 0) {
                        union(minLabel, labels[nZ]);
                    }
                }
            }
        }

        // Yield check every slice: maybeYield itself budgets the actual
        // yields, so this only costs a performance.now() per slice —
        // per-10-slice checks produced 100+ ms uninterrupted blocks that
        // starved pointer events during previews.
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    // Second pass: flatten labels using union-find. rootToLabel is a Map,
    // not an Int32Array(N): real volumes have a handful of components, and
    // the full-size array was 4 bytes/voxel of pure transient allocation
    // (~210 MB at 512×512×200, an OOM-ceiling concern on big CTs). The
    // last-root memo keeps Map lookups off the hot path — consecutive
    // foreground voxels almost always share a root.
    const rootToLabel = new Map<number, number>();
    // Voxel count per final label, accumulated here so callers don't need
    // their own full-volume counting pass. Index 0 (background) unused.
    const sizes: number[] = [0];
    let nextLabel = 1;
    let lastRoot = -1;
    let lastLabel = 0;

    for (let z = 0; z < nz; z++) {
        const slice = sdfU8[z];
        const zOffset = z * plane;

        for (let localIdx = 0; localIdx < plane; localIdx++) {
            const i = zOffset + localIdx;
            if (isForeground(slice[localIdx])) {
                const root = find(labels[i]);
                let label: number;
                if (root === lastRoot) {
                    label = lastLabel;
                } else {
                    const existing = rootToLabel.get(root);
                    if (existing === undefined) {
                        label = nextLabel++;
                        rootToLabel.set(root, label);
                        sizes.push(0);
                    } else {
                        label = existing;
                    }
                    lastRoot = root;
                    lastLabel = label;
                }
                sizes[label]++;
                labels[i] = label;
            } else {
                labels[i] = 0;
            }
        }

        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    return {
        labels,
        numComponents: nextLabel - 1,
        componentSizes: Int32Array.from(sizes),
    };
}

export interface RemoveSmallPartsResult {
    sdfU8: Uint8Array[];
    numComponentsFound: number;
    numComponentsRemoved: number;
    /**
     * True when the never-empty rule kept a component the size floor had
     * rejected — every candidate was below the floor, so the largest was
     * put back (see {@link RemoveSmallPartsOptions.allowEmptyResult}).
     *
     * Reported so the caller can SAY SO: the structure that is left
     * violates the minimum size the user set, and nothing in a "removed 2
     * of 3 parts" count reveals that the floor was overridden. Always
     * false where no floor was applied at all.
     */
    sizeFloorOverridden: boolean;
}

/**
 * Optional size floor fused into {@link keepLargestComponents}. When given,
 * the rank survivors are additionally judged against a minimum component
 * size — equivalent to a subsequent {@link removeSmallParts} call over the
 * same connectivity, but sharing the one labeling pass. The invariant that
 * makes one labeling sufficient: rank selection removes only whole
 * components, so the survivors' voxel sets and sizes are exactly what a
 * re-labeling of the intermediate volume would find.
 *
 * Units follow {@link removeSmallParts}: mm edge lengths, and a floor that
 * is a volume (mm³) under '3d' connectivity, an in-plane area (mm²) under
 * '2d'.
 */
export interface ComponentSizeFloor {
    /** Voxel edge length in X (mm). */
    resXMm: number;
    /** Voxel edge length in Y (mm). */
    resYMm: number;
    /** Voxel edge length in Z (mm). */
    resZMm: number;
    /** Minimum size to keep — mm³ (volume) in '3d', mm² (in-plane area) in '2d'. */
    minComponentSizeMm: number;
    /**
     * Opt out of the never-empty safety rule; see
     * {@link RemoveSmallPartsOptions.allowEmptyResult}. Default false.
     */
    allowEmptyResult?: boolean;
}

/**
 * Removed-component fill value: the encoding floor, so the voxel stops
 * claiming to be a hair outside a surface that is no longer there. Any value
 * below the iso leaves the contour where it is; this is the furthest out
 * available, and the only one a later in-place 2D smoothing cannot average
 * back over the iso.
 */
export const REMOVED_VALUE_OUTSIDE = 0;

/** Cavity fill value: saturated inside, the furthest-in value available. */
export const FILLED_VALUE = 255;

/**
 * Write the output volume for a component-removal operation: bulk-copy each
 * input slice (memcpy), then patch only the voxels of dropped components to
 * `removedValue`. The overwhelmingly common case is "copy the input value",
 * so patching the exceptions keeps the output pass to one memcpy per slice
 * plus a sparse fixup.
 */
async function writeSlicesWithoutComponents(
    sdfU8: Uint8Array[],
    labels: Int32Array,
    keepComponent: Uint8Array,
    nx: number,
    ny: number,
    nz: number,
    removedValue: number,
    signal?: AbortSignal
): Promise<Uint8Array[]> {
    const plane = nx * ny;
    const outU8: Uint8Array[] = new Array(nz);
    let lastYieldTick = performance.now();
    for (let z = 0; z < nz; z++) {
        const out = new Uint8Array(sdfU8[z]);
        const zOffset = z * plane;
        for (let i = 0; i < plane; i++) {
            const label = labels[zOffset + i];
            if (label > 0 && !keepComponent[label]) {
                out[i] = removedValue;
            }
        }
        outU8[z] = out;
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }
    return outU8;
}

/**
 * Apply a minimum-size floor to the components currently marked 1 in
 * `keepComponent` (the "candidates"), clearing those below it. Shared by
 * {@link removeSmallParts} and the fused floor of
 * {@link keepLargestComponents} so both enforce the floor — and the
 * never-empty rule — with identical arithmetic and identical tie-breaks.
 *
 * Never-empty rule (unless `allowEmptyResult`): if EVERY candidate is below
 * the floor, the largest candidate is retained. Ties resolve to the smaller
 * label, i.e. the component encountered first in scan order — deterministic
 * for identical inputs.
 *
 * @returns the number of candidates actually removed, and whether the
 * never-empty rule had to OVERRIDE the floor to get there. The second half
 * is not an internal detail: the retained part is below the size the user
 * asked for, and a correction applied to a clinical value has to be
 * surfaced rather than absorbed.
 */
function applySizeFloor(
    keepComponent: Uint8Array,
    componentSizes: Int32Array,
    numComponents: number,
    perVoxelSize: number,
    minComponentSizeMm: number,
    allowEmptyResult: boolean
): { numRemoved: number; sizeFloorOverridden: boolean } {
    let numCandidates = 0;
    let numRemoved = 0;
    let largestCandidate = 0;
    for (let c = 1; c <= numComponents; c++) {
        if (keepComponent[c] !== 1) {
            continue;
        }
        numCandidates++;
        if (
            largestCandidate === 0 ||
            componentSizes[c] > componentSizes[largestCandidate]
        ) {
            largestCandidate = c;
        }
        if (componentSizes[c] * perVoxelSize < minComponentSizeMm) {
            keepComponent[c] = 0;
            numRemoved++;
        }
    }
    if (
        !allowEmptyResult &&
        numCandidates > 0 &&
        numRemoved === numCandidates
    ) {
        keepComponent[largestCandidate] = 1;
        numRemoved--;
        return { numRemoved, sizeFloorOverridden: true };
    }
    return { numRemoved, sizeFloorOverridden: false };
}

/** Behaviour switches for {@link removeSmallParts}. */
export interface RemoveSmallPartsOptions {
    /**
     * Opt out of the never-empty safety rule. Default (false/omitted): if
     * every component is below the floor the largest is retained, so the
     * operation can never silently delete the whole structure — this is
     * what the interactive "Remove Small Parts" tool relies on.
     *
     * Set true only where the caller enforces its own empty-result refusal
     * and the floor must therefore be strict (the threshold-segmentation
     * pipeline does: its preview and apply paths refuse an empty result
     * with `thresholdSeg.emptyResult`). With it, a volume whose only
     * component is below the floor comes back empty rather than delivering
     * a structure that violates the configured minimum while provenance
     * records the minimum as applied.
     */
    allowEmptyResult?: boolean;
    /** See {@link ConnectedComponentOptions.disableSdfSkip}. */
    disableSdfSkip?: boolean;
}

/**
 * Remove small connected components from an SDF volume.
 * Components whose size falls below the threshold are removed.
 *
 * The size metric depends on the connectivity mode, because a component's
 * dimensionality differs between them:
 * - '3D': each component spans the volume, so its size is a VOLUME (mm³) =
 *   voxel count × resX × resY × resZ.
 * - '2D': each component lives in a single native slice, so its size is an
 *   in-plane AREA (mm²) = voxel count × resX × resY. Slice thickness is
 *   deliberately excluded so the threshold does not depend on it.
 *
 * All quantities here are in mm / mm² / mm³ (domain units); the caller
 * converts to/from cm² / cm³ at the UI boundary.
 *
 * Empty-result behaviour is a contract with two documented halves, see
 * {@link RemoveSmallPartsOptions.allowEmptyResult}: by default the ROI is
 * never emptied (the largest component survives even when it is below the
 * floor); with the opt-out the floor is strict and judges every component,
 * a sole one included.
 *
 * When nothing needs removing (no component, or every component clears the
 * floor) the INPUT slice arrays are returned as-is — no defensive copy.
 * The input is consumed: callers must treat both the input and the result
 * as owned by the result and not mutate either afterwards. (The former
 * full copy cost ~50 ms + GC on the normal single-component body-contour
 * case for no benefit.)
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param resXMm Voxel edge length in X (mm)
 * @param resYMm Voxel edge length in Y (mm)
 * @param resZMm Voxel edge length in Z (mm)
 * @param minComponentSizeMm Minimum size to keep — mm³ (volume) in 3D, mm² (in-plane area) in 2D
 * @param mode '3D' evaluates components by volume over the whole volume; '2D' by in-plane area per native slice
 * @param signal Optional AbortSignal for cancellation
 * @param options Empty-result and skip-optimization control
 * @returns Result containing processed SDF and statistics
 */
export async function removeSmallParts(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    resXMm: number,
    resYMm: number,
    resZMm: number,
    minComponentSizeMm: number,
    mode: PostProcessingMode,
    signal?: AbortSignal,
    options?: RemoveSmallPartsOptions
): Promise<RemoveSmallPartsResult> {
    // Label connected components of the inside (foreground). In 2D mode each
    // component is confined to one native slice, so a stray single-slice
    // island can be dropped without affecting the rest of the volume.
    const { labels, numComponents, componentSizes } =
        await labelConnectedComponents(
            sdfU8,
            nx,
            ny,
            nz,
            threshold,
            true, // labelInside = true
            signal,
            {
                connectivity: connectivityForMode(mode),
                disableSdfSkip: options?.disableSdfSkip,
            }
        );

    if (numComponents === 0) {
        // Empty ROI — nothing to judge; the input is passed through (see
        // the consumed-input contract above).
        return {
            sdfU8,
            numComponentsFound: 0,
            numComponentsRemoved: 0,
            sizeFloorOverridden: false,
        };
    }

    // Size per voxel: a 2D component's size is its in-plane area (mm²), a 3D
    // component's is its volume (mm³). See the function doc for the rationale.
    // Component voxel counts come from the labeler — no separate
    // full-volume counting pass.
    const perVoxelSize =
        mode === '2D' ? resXMm * resYMm : resXMm * resYMm * resZMm;

    const keepComponent = new Uint8Array(numComponents + 1).fill(1, 1);
    const { numRemoved: numComponentsRemoved, sizeFloorOverridden } =
        applySizeFloor(
            keepComponent,
            componentSizes,
            numComponents,
            perVoxelSize,
            minComponentSizeMm,
            options?.allowEmptyResult === true
        );

    if (numComponentsRemoved === 0) {
        // Every component clears the floor (or the never-empty rule put the
        // only candidate back) — pass the input through (consumed-input
        // contract), keeping the common single-component case
        // allocation-free.
        return {
            sdfU8,
            numComponentsFound: numComponents,
            numComponentsRemoved: 0,
            sizeFloorOverridden,
        };
    }

    const outU8 = await writeSlicesWithoutComponents(
        sdfU8,
        labels,
        keepComponent,
        nx,
        ny,
        nz,
        REMOVED_VALUE_OUTSIDE,
        signal
    );

    return {
        sdfU8: outU8,
        numComponentsFound: numComponents,
        numComponentsRemoved,
        sizeFloorOverridden,
    };
}

/**
 * Keep only the N largest connected components (by voxel count — with a
 * uniform voxel volume that ordering equals volume ordering) and remove the
 * rest. The body-contour default (N = 1) removes the treatment couch and
 * any other detached object in one deterministic step.
 *
 * Connectivity follows {@link ConnectedComponentOptions}: in '3d' the N
 * largest components of the whole volume are kept; in '2d' the N largest
 * are kept PER SLICE — a global pick would keep components on at most N
 * slices and erase every other slice's cross-section, which is never the
 * clinical intent of per-slice cleanup.
 *
 * Ties (equal sizes) resolve to the smaller label, i.e. the component
 * encountered first in scan order — deterministic for identical inputs.
 *
 * Shares the consumed-input contract of {@link removeSmallParts}: when
 * nothing needs removing the input arrays are returned as-is.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param keepCount Number of components to keep (integer ≥ 1); when fewer
 *   components exist, all are kept
 * @param signal Optional AbortSignal for cancellation
 * @param options Connectivity mode ('2d' keeps N largest per slice) and
 *   skip control
 * @param sizeFloor Optional fused minimum-size stage: after the rank
 *   selection, kept components below the floor are also removed, exactly
 *   as a subsequent {@link removeSmallParts} over the same connectivity
 *   would — never-empty default and `allowEmptyResult` opt-out included —
 *   but without a second full-volume labeling pass
 */
export async function keepLargestComponents(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    keepCount: number,
    signal?: AbortSignal,
    options?: ConnectedComponentOptions,
    sizeFloor?: ComponentSizeFloor
): Promise<RemoveSmallPartsResult> {
    if (!Number.isInteger(keepCount) || keepCount < 1) {
        // Fail fast: a fractional or non-positive keep count is a caller
        // bug, not a value to clamp silently (SaMD: no hidden "fixes").
        throw new Error(
            `keepLargestComponents: keepCount must be an integer >= 1, ` +
                `got ${keepCount}`
        );
    }
    const plane = nx * ny;
    const connect3d = (options?.connectivity ?? '3d') === '3d';

    const { labels, numComponents, componentSizes } =
        await labelConnectedComponents(
            sdfU8,
            nx,
            ny,
            nz,
            threshold,
            true, // labelInside = true
            signal,
            options
        );

    if (numComponents <= keepCount && sizeFloor === undefined) {
        // Every component survives regardless of grouping — pass the
        // input through (consumed-input contract).
        return {
            sdfU8,
            numComponentsFound: numComponents,
            numComponentsRemoved: 0,
            sizeFloorOverridden: false,
        };
    }

    // Sort size-descending, label-ascending on ties (labels are assigned
    // in scan order, so this is deterministic for identical inputs).
    const bySizeDesc = (a: number, b: number) =>
        componentSizes[b] - componentSizes[a] || a - b;

    const keepComponent = new Uint8Array(numComponents + 1);
    let keptCount = 0;
    if (numComponents <= keepCount) {
        // Rank cut removes nothing; only the fused floor below can.
        keepComponent.fill(1, 1);
        keptCount = numComponents;
    } else if (connect3d) {
        const order = Array.from(
            { length: numComponents },
            (_, i) => i + 1
        ).sort(bySizeDesc);
        for (let i = 0; i < keepCount; i++) {
            keepComponent[order[i]] = 1;
        }
        keptCount = keepCount;
    } else {
        // '2d': labels never span slices, so grouping labels by slice via
        // one scan of the label volume is exact.
        let lastYieldTick = performance.now();
        for (let z = 0; z < nz; z++) {
            const zOffset = z * plane;
            const sliceLabels = new Set<number>();
            for (let i = 0; i < plane; i++) {
                const label = labels[zOffset + i];
                if (label > 0) {
                    sliceLabels.add(label);
                }
            }
            const order = Array.from(sliceLabels).sort(bySizeDesc);
            const n = Math.min(keepCount, order.length);
            for (let i = 0; i < n; i++) {
                keepComponent[order[i]] = 1;
            }
            keptCount += n;
            lastYieldTick = await maybeYield(lastYieldTick, signal);
        }
    }

    // Fused minimum-size floor over the rank survivors — identical
    // arithmetic, tie-breaks and never-empty rule as a subsequent
    // removeSmallParts call over the same connectivity (shared helper).
    let sizeFloorOverridden = false;
    if (sizeFloor !== undefined) {
        const perVoxelSize = connect3d
            ? sizeFloor.resXMm * sizeFloor.resYMm * sizeFloor.resZMm
            : sizeFloor.resXMm * sizeFloor.resYMm;
        const floor = applySizeFloor(
            keepComponent,
            componentSizes,
            numComponents,
            perVoxelSize,
            sizeFloor.minComponentSizeMm,
            sizeFloor.allowEmptyResult === true
        );
        keptCount -= floor.numRemoved;
        sizeFloorOverridden = floor.sizeFloorOverridden;
    }

    const numComponentsRemoved = numComponents - keptCount;
    if (numComponentsRemoved === 0) {
        // Nothing removed by either criterion — consumed-input contract.
        return {
            sdfU8,
            numComponentsFound: numComponents,
            numComponentsRemoved: 0,
            sizeFloorOverridden,
        };
    }

    const outU8 = await writeSlicesWithoutComponents(
        sdfU8,
        labels,
        keepComponent,
        nx,
        ny,
        nz,
        REMOVED_VALUE_OUTSIDE,
        signal
    );

    return {
        sdfU8: outU8,
        numComponentsFound: numComponents,
        numComponentsRemoved,
        sizeFloorOverridden,
    };
}

export interface FillCavitiesResult {
    sdfU8: Uint8Array[];
    /** Number of voxels filled; 0 means no enclosed cavity was found. */
    filledVoxelCount: number;
}

/** Behaviour switches for {@link fillCavities}. */
export interface FillCavitiesOptions {
    /** See {@link ConnectedComponentOptions.disableSdfSkip}. */
    disableSdfSkip?: boolean;
}

/**
 * Fill cavities (holes) in an SDF volume. Any enclosed cavity inside the
 * structure will be filled.
 *
 * '3D' uses union-find background labeling over the whole volume with
 * amortized near-linear complexity (O(n α(n)) with inverse Ackermann α);
 * the enclosing boundary is the six faces of the volume, so a cavity must
 * be enclosed in all three dimensions.
 *
 * '2D' delegates to {@link fillCavities2D} — the per-slice border flood
 * fill, the single implementation of the per-slice semantics.
 *
 * Both paths always allocate their output; neither consumes the input.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param mode '3D' fills cavities enclosed by the whole volume; '2D' fills holes enclosed within each native slice
 * @param signal Optional AbortSignal for cancellation
 * @param options Skip-optimization control (3D path only; the 2D flood fill needs no such assumption)
 * @returns Processed SDF slices and the count of voxels filled (0 if no enclosed cavity was found — e.g. a ≤2-slice volume in 3D mode, which has no interior along Z)
 */
export async function fillCavities(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    mode: PostProcessingMode,
    signal?: AbortSignal,
    options?: FillCavitiesOptions
): Promise<FillCavitiesResult> {
    if (mode === '2D') {
        return fillCavities2D(sdfU8, nx, ny, nz, threshold, signal);
    }

    const plane = nx * ny;

    let lastYieldTick = performance.now();

    // Label connected components of the background (outside regions)
    // labelInside=false means we label voxels where sdf < threshold
    const { labels, numComponents } = await labelConnectedComponents(
        sdfU8,
        nx,
        ny,
        nz,
        threshold,
        false, // labelInside = false (label the background/outside)
        signal,
        { connectivity: '3d', disableSdfSkip: options?.disableSdfSkip }
    );

    if (numComponents === 0) {
        // No background - return copy of input, nothing filled
        return {
            sdfU8: sdfU8.map((slice) => new Uint8Array(slice)),
            filledVoxelCount: 0,
        };
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Find which components touch the boundary (those are exterior, not cavities).
    // A background component that reaches the boundary is the exterior "air"; any
    // background component that does not is an enclosed cavity to be filled.
    const touchesBoundary = new Uint8Array(numComponents + 1);

    // The enclosing boundary is the six faces of the volume: the Z=0 and
    // Z=nz-1 faces in full, plus the in-plane edges of the interior slices.
    for (const z of [0, nz - 1]) {
        const zOffset = z * plane;
        for (let i = 0; i < plane; i++) {
            const label = labels[zOffset + i];
            if (label > 0) {
                touchesBoundary[label] = 1;
            }
        }
    }
    // Y=0 and Y=ny-1 rows of the interior slices (full width)
    for (let z = 1; z < nz - 1; z++) {
        const zOffset = z * plane;
        for (const y of [0, ny - 1]) {
            const yOffset = y * nx;
            for (let x = 0; x < nx; x++) {
                const label = labels[zOffset + yOffset + x];
                if (label > 0) {
                    touchesBoundary[label] = 1;
                }
            }
        }
    }
    // X=0 and X=nx-1 columns of the interior slices (corners already covered)
    for (let z = 1; z < nz - 1; z++) {
        const zOffset = z * plane;
        for (let y = 1; y < ny - 1; y++) {
            const yOffset = y * nx;
            for (const x of [0, nx - 1]) {
                const label = labels[zOffset + yOffset + x];
                if (label > 0) {
                    touchesBoundary[label] = 1;
                }
            }
        }
    }

    lastYieldTick = await maybeYield(lastYieldTick, signal);

    // Create output - fill any background component that doesn't touch
    // boundary. Bulk-copy each slice, then patch only the cavity voxels.
    let filledVoxelCount = 0;
    const outU8: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        const out = new Uint8Array(sdfU8[z]);
        const zOffset = z * plane;
        for (let i = 0; i < plane; i++) {
            const label = labels[zOffset + i];
            // label === 0 is the structure itself, a boundary-touching
            // label is exterior background — both keep their value.
            if (label > 0 && !touchesBoundary[label]) {
                out[i] = FILLED_VALUE;
                filledVoxelCount++;
            }
        }
        outU8[z] = out;

        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    return { sdfU8: outU8, filledVoxelCount };
}

/**
 * Fill cavities per slice (2D): on each Z slice independently, any background
 * region not connected to the slice border is filled. This is the
 * implementation behind `fillCavities(..., '2D', ...)`; it is exported
 * directly for pipelines whose stage order is fixed to per-slice semantics.
 *
 * This differs from 3D fill in what counts as "outside": 3D fill treats a
 * lung-like region as exterior because it connects to outside air through the
 * airway on other slices, so it stays unfilled. For a patient outline
 * (EXTERNAL) the clinical convention is the opposite — everything enclosed by
 * the body on a slice belongs to the body — so the boundary test here is the
 * in-plane slice border only, and the Z faces of the volume are deliberately
 * NOT boundaries (an enclosed region on the first or last slice is still a
 * cavity).
 *
 * Implemented as a per-slice border flood fill with two plane-sized buffers
 * reused across slices, NOT via the full-volume union-find labeler: the
 * operation is inherently per-slice, and the labeler's three transient
 * Int32Array(N) allocations (~630 MB at 512×512×200) made this the second
 * most expensive CPU stage (measured 2.9 s → 0.6 s). The output is
 * semantically identical (pinned by golden equivalence tests against the
 * labeling implementation): a background (< threshold) voxel is exterior
 * exactly when a 4-connected in-plane path links it to the slice border,
 * and every unreachable background voxel is a cavity. The flood fill also
 * needs no SDF-skip caveat — it visits every voxel, so it is correct on
 * hard binary masks as well as metric fields.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param signal Optional AbortSignal for cancellation
 * @returns Processed SDF slices (always newly allocated) and the count of voxels filled
 */
export async function fillCavities2D(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    signal?: AbortSignal
): Promise<FillCavitiesResult> {
    const plane = nx * ny;
    const outU8: Uint8Array[] = new Array(nz);
    let filledVoxelCount = 0;

    // Reused across slices: `reached` marks border-connected background,
    // `stack` is the DFS worklist. Voxels are marked when pushed, so the
    // stack can never hold more than one entry per plane voxel.
    const reached = new Uint8Array(plane);
    const stack = new Int32Array(plane);

    let lastYieldTick = performance.now();
    for (let z = 0; z < nz; z++) {
        const slice = sdfU8[z];
        reached.fill(0);
        let top = 0;

        const push = (i: number) => {
            if (!reached[i] && slice[i] < threshold) {
                reached[i] = 1;
                stack[top++] = i;
            }
        };

        // Seed with every background voxel on the slice border. The Z
        // faces of the volume are deliberately NOT boundaries (see the
        // function contract above).
        for (let x = 0; x < nx; x++) {
            push(x);
            push((ny - 1) * nx + x);
        }
        for (let y = 1; y < ny - 1; y++) {
            push(y * nx);
            push(y * nx + nx - 1);
        }

        // 4-connected in-plane flood fill.
        while (top > 0) {
            const i = stack[--top];
            const x = i % nx;
            if (x > 0) {
                push(i - 1);
            }
            if (x < nx - 1) {
                push(i + 1);
            }
            if (i >= nx) {
                push(i - nx);
            }
            if (i < plane - nx) {
                push(i + nx);
            }
        }

        // Bulk-copy the slice, then patch only the cavity voxels
        // (background the border fill never reached).
        const out = new Uint8Array(slice);
        for (let i = 0; i < plane; i++) {
            if (slice[i] < threshold && !reached[i]) {
                out[i] = FILLED_VALUE;
                filledVoxelCount++;
            }
        }
        outU8[z] = out;

        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    return { sdfU8: outU8, filledVoxelCount };
}

/**
 * Direction of a {@link cropToBounds} run: keep what the box contains, or
 * clear it.
 */
export type CropDirection = 'keep-inside' | 'delete-inside';

export interface CropToBoundsResult {
    /** Always newly allocated; the input is never mutated. */
    sdfU8: Uint8Array[];
    /**
     * Voxels that were INSIDE the structure and are now background. Voxels
     * already outside are not counted — clearing them is not a change, and
     * the success toast would otherwise report a number the user cannot see.
     */
    clearedVoxelCount: number;
    /**
     * Voxels that are INSIDE the structure in the output — what the run
     * left of it. Zero means nothing of the structure survived, which the
     * wrappers read differently, so it is reported rather than judged here.
     */
    retainedVoxelCount: number;
}

/**
 * Meet one slice of an SDF field with a region's own field, in place.
 * Returns whether anything changed.
 *
 * The single metric region cut: each voxel takes the lesser of its own
 * value and the region's signed distance on the SDF byte scale — the same
 * min-of-signed-distances a boolean AND performs. The contour therefore
 * lands BETWEEN voxel centres rather than on a voxel face, and where the
 * region's bounds actually are rather than at a window snapped outward to
 * whole voxels.
 *
 * `invert` cuts to the region's COMPLEMENT: the sign of the distance is
 * flipped, so what the region contains is what goes.
 *
 * @param bounds Fractional voxel-space bounds in the field's own grid.
 * @param resMm Voxel spacing of that grid — the byte scale is millimetres,
 *   so anisotropy has to reach the distance.
 * @param bytesPerMm The texture's own `SDF_ISO_THRESHOLD / margin`.
 */
export function cutSliceToRegion(
    slice: Uint8Array,
    nx: number,
    ny: number,
    z: number,
    bounds: VoxelBounds,
    shape: VoiShape,
    resMm: readonly [number, number, number],
    bytesPerMm: number,
    invert = false
): boolean {
    let changed = false;
    for (let y = 0; y < ny; y++) {
        const row = y * nx;
        for (let x = 0; x < nx; x++) {
            const current = slice[row + x];
            if (current === 0) {
                // Already at the encoding floor; no meet can lower it.
                continue;
            }
            const insideMm = regionInsideDistanceMm(
                bounds,
                shape,
                resMm,
                x,
                y,
                z
            );
            const signedMm = invert ? -insideMm : insideMm;
            const regionValue = Math.max(
                0,
                Math.min(
                    255,
                    Math.round(SDF_ISO_THRESHOLD + signedMm * bytesPerMm)
                )
            );
            if (regionValue < current) {
                slice[row + x] = regionValue;
                changed = true;
            }
        }
    }
    return changed;
}

/**
 * Cut `field` to the region `bounds` describes under `shape`, in place.
 * Returns the indices of the slices it rewrote, so the caller re-uploads
 * only those.
 *
 * The final cut of a restricted extraction: smoothing and the margin
 * spread the metric SDF past whatever the mask selected (the blur mixes
 * inside values across the boundary), so without it the applied contour
 * leaks beyond the region the user drew.
 */
export function cutFieldToRegion(
    field: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    bounds: VoxelBounds,
    shape: VoiShape,
    resMm: readonly [number, number, number],
    bytesPerMm: number
): number[] {
    const changedSlices: number[] = [];
    for (let z = 0; z < nz; z++) {
        if (
            cutSliceToRegion(
                field[z],
                nx,
                ny,
                z,
                bounds,
                shape,
                resMm,
                bytesPerMm
            )
        ) {
            changedSlices.push(z);
        }
    }
    return changedSlices;
}

/**
 * Restrict an SDF volume to a region, or clear that region.
 *
 * METRIC, and against the region's EXACT fractional bounds: each voxel
 * takes the lesser of its own value and the region's signed distance on
 * the SDF byte scale — the same min-of-signed-distances a boolean AND
 * performs. Two things follow that a whole-voxel cut at an outward-snapped
 * window got wrong: the contour lands BETWEEN voxel centres, so a boundary
 * is not a staircase; and it lands where the user drew the region rather
 * than up to a voxel per face outside it, which on PET spacing is
 * millimetres. It is also what threshold segmentation cuts at, so one box
 * drawn in the shared region overlay means one result whichever operation
 * reads it.
 *
 * @param bounds Fractional voxel-space bounds in the field's own grid.
 * @param resMm Voxel spacing of that grid — the byte scale is millimetres,
 *   so anisotropy has to reach the distance.
 * @param bytesPerMm The texture's own `SDF_ISO_THRESHOLD / margin`.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X
 * @param ny Number of voxels in Y
 * @param nz Number of voxels in Z
 * @param threshold SDF threshold (typically 127.5)
 * @param range Voxel window, half-open per axis
 * @param direction Keep what the window contains, or clear it
 * @param signal Optional AbortSignal for cancellation
 * @returns Processed SDF slices (always newly allocated) with the cleared
 * and retained foreground counts
 */
export async function cropToBounds(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    bounds: VoxelBounds,
    resMm: readonly [number, number, number],
    bytesPerMm: number,
    direction: CropDirection,
    signal?: AbortSignal
): Promise<CropToBoundsResult> {
    const keepInside = direction === 'keep-inside';
    const outU8: Uint8Array[] = new Array(nz);
    let clearedVoxelCount = 0;
    let retainedVoxelCount = 0;

    let lastYieldTick = performance.now();
    for (let z = 0; z < nz; z++) {
        // Bulk-copy then patch: the common case is "keep the input value",
        // exactly as writeSlicesWithoutComponents assumes.
        const slice = sdfU8[z];
        const out = new Uint8Array(slice);
        outU8[z] = out;

        cutSliceToRegion(
            out,
            nx,
            ny,
            z,
            bounds,
            'box',
            resMm,
            bytesPerMm,
            !keepInside
        );

        // Counted against the source: only a voxel that WAS inside the
        // structure and is no longer counts as cleared, so the report never
        // names voxels the user could not see.
        for (let i = 0; i < slice.length; i++) {
            if (slice[i] >= threshold) {
                if (out[i] >= threshold) {
                    retainedVoxelCount++;
                } else {
                    clearedVoxelCount++;
                }
            }
        }

        // Yield check every slice: maybeYield itself budgets the actual
        // yields, so a small volume still runs straight through.
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    return { sdfU8: outU8, clearedVoxelCount, retainedVoxelCount };
}
